import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Mic, Clock } from "lucide-react";

function getScoreBarColor(value) {
  if (value >= 80) return 'bg-[#14B8A6]';
  if (value >= 60) return 'bg-[#F59E0B]';
  return 'bg-red-500';
}

export function Sidebar({ analytics, recentSessions }) {
  return (
    <div className="space-y-6">
      {/* Competencies */}
      <Card className="premium-card fade-up">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg font-bold text-[#0B1F3B]">Skill Breakdown</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {analytics?.competencies && Object.entries(analytics.competencies).map(([key, value]) => (
            <div key={key}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-slate-600 capitalize">{key.replace(/_/g, ' ')}</span>
                <span className="text-sm font-semibold text-[#0B1F3B]">{value}%</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all ${getScoreBarColor(value)}`}
                  style={{ width: `${value}%` }}
                ></div>
              </div>
            </div>
          ))}
          {!analytics?.competencies && (
            <p className="text-sm text-slate-400 text-center py-4">Complete practice sessions to see skills</p>
          )}
        </CardContent>
      </Card>

      {/* Recent Sessions */}
      <Card className="premium-card fade-up">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-bold text-[#0B1F3B]">Recent Practice</CardTitle>
            <Link to="/history" className="text-sm text-[#2563EB] hover:underline" data-testid="view-history-link">
              View all
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          {recentSessions.length > 0 ? (
            <div className="space-y-3">
              {recentSessions.map((session) => (
                <Link 
                  key={session.id} 
                  to={session.status === 'evaluated' ? `/evaluation/${session.id}` : `/simulator?resume=${session.id}`}
                  className="block"
                >
                  <div className="p-3 rounded-xl border border-slate-200/60 hover:bg-slate-50 transition-colors">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-[#0B1F3B]">{session.scenario_name}</span>
                      {session.total_score && (
                        <span className="text-sm font-bold text-[#2563EB]">{session.total_score}%</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-400">
                      <Clock className="w-3 h-3" />
                      {new Date(session.started_at).toLocaleDateString()}
                      <Badge variant="outline" className="text-xs">
                        {session.status === 'evaluated' ? 'Reviewed' : session.status}
                      </Badge>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <Mic className="w-10 h-10 text-slate-200 mx-auto mb-2" />
              <p className="text-sm text-slate-400">No practice sessions yet</p>
              <Link to="/simulator">
                <Button variant="link" className="text-[#2563EB] text-sm">Start your first practice</Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
